import React from 'react';
import * as Headless from '@headlessui/react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  EllipsisVerticalIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { Badge } from '@oysterjs/ui/badge';
import { Button } from '@oysterjs/ui/button';
import { Input, InputGroup } from '@oysterjs/ui/input';
import { Select } from '@oysterjs/ui/select';
import {
  AgencyUserFieldsFragment,
  AgencyUserState,
  Resource,
  Role,
  Action,
  Scope
} from '../../../api/graphql';
import { usePermissions } from '@oysterjs/ais/components/permissions';

/**
 * Badge component for displaying user state (Active/Disabled)
 */
export const UserStateBadge = ({ state }: { state: AgencyUserState }) => {
  switch (state) {
    case AgencyUserState.Active:
      return <Badge color="sky">Active</Badge>;
    case AgencyUserState.Disabled:
      return <Badge color="neutral">Disabled</Badge>;
  }
};

/** At most two letters: the first and last words' initials. */
export const initialsOf = (name: string) => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const letters = words.length > 1 ? [words[0], words[words.length - 1]] : words;
  return letters
    .map((word) => word[0])
    .join('')
    .toUpperCase();
};

const ROLE_BADGES: Record<Role, { label: string; color: 'violet' | 'zinc' }> = {
  [Role.Admin]: { label: 'Admin', color: 'violet' },
  [Role.User]: { label: 'User', color: 'zinc' }
};

type UserListItemProps = {
  user: AgencyUserFieldsFragment;
  onDisable?: (userId: string) => void;
  onEnable?: (userId: string) => void;
  onEdit?: (user: AgencyUserFieldsFragment) => void;
  /**
   * If true, renders a more compact version (smaller avatar, tighter spacing)
   */
  compact?: boolean;
  /**
   * The scope for permission checks (ORGANIZATION for parent agency, SUB_ORGANIZATION for child agencies)
   */
  scope: Scope;
};

/**
 * A single user row item with avatar, name, state badge, and context menu
 */
export const UserListItem: React.FC<UserListItemProps> = ({
  user,
  onDisable,
  onEnable,
  onEdit,
  compact = false,
  scope
}) => {
  const { hasScopedPermission } = usePermissions();

  return (
    <li
      className={clsx(
        'flex justify-between first:pt-0 last:pb-0',
        compact ? 'px-4 py-3' : 'px-4 py-4 sm:px-6'
      )}
    >
      <div className={clsx('flex min-w-0', compact ? 'gap-x-3' : 'gap-x-4')}>
        <div
          className={clsx(
            'flex-none rounded-full bg-neutral-100 dark:bg-neutral-700 flex items-center justify-center text-neutral-700 dark:text-neutral-300 font-semibold',
            compact ? 'size-10 text-sm' : 'size-12 text-lg'
          )}
        >
          {initialsOf(user.name)}
        </div>
        <div className="min-w-0 flex-auto">
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 font-semibold text-neutral-900 dark:text-white text-sm/6">
            {user.name}
            <UserStateBadge state={user.state} />
            {user.roles.map((role) => (
              <Badge key={role} color={ROLE_BADGES[role].color}>
                {ROLE_BADGES[role].label}
              </Badge>
            ))}
          </p>
          <p className="truncate text-neutral-500 dark:text-neutral-400 text-xs/5">{user.email}</p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-x-6">
        <Headless.Menu as="div" className="relative flex-none">
          <Headless.MenuButton className="-m-2.5 block p-2.5 text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white">
            <span className="sr-only">Open options</span>
            <EllipsisVerticalIcon aria-hidden="true" className="size-5" />
          </Headless.MenuButton>
          <Headless.MenuItems
            transition
            className="absolute right-0 z-10 mt-2 w-32 origin-top-right rounded-md bg-white dark:bg-neutral-800 py-2 shadow-lg ring-1 ring-neutral-900/5 dark:ring-white/10 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
          >
            {user.state === AgencyUserState.Active && onDisable && (
              <Headless.MenuItem
                disabled={!hasScopedPermission(scope, Resource.AgencyUser, Action.Disable)}
              >
                <span
                  role="button"
                  onClick={() => onDisable(user.id)}
                  className="block px-3 py-1 text-sm/6 text-neutral-900 dark:text-white data-[focus]:bg-neutral-50 dark:data-[focus]:bg-white/5 data-[focus]:outline-none data-[disabled]:opacity-50 data-[disabled]:cursor-default"
                >
                  Disable<span className="sr-only">, {user.name}</span>
                </span>
              </Headless.MenuItem>
            )}
            {user.state === AgencyUserState.Disabled && onEnable && (
              <Headless.MenuItem
                disabled={!hasScopedPermission(scope, Resource.AgencyUser, Action.Enable)}
              >
                <span
                  role="button"
                  onClick={() => onEnable(user.id)}
                  className="block px-3 py-1 text-sm/6 text-neutral-900 dark:text-white data-[focus]:bg-neutral-50 dark:data-[focus]:bg-white/5 data-[focus]:outline-none data-[disabled]:opacity-50 data-[disabled]:cursor-default"
                >
                  Enable<span className="sr-only">, {user.name}</span>
                </span>
              </Headless.MenuItem>
            )}
            {onEdit && (
              <Headless.MenuItem
                disabled={!hasScopedPermission(scope, Resource.AgencyUser, Action.Update)}
              >
                <span
                  role="button"
                  onClick={() => onEdit(user)}
                  className="block px-3 py-1 text-sm/6 text-neutral-900 dark:text-white data-[focus]:bg-neutral-50 dark:data-[focus]:bg-white/5 data-[focus]:outline-none data-[disabled]:opacity-50 data-[disabled]:cursor-default"
                >
                  Edit<span className="sr-only">, {user.name}</span>
                </span>
              </Headless.MenuItem>
            )}
          </Headless.MenuItems>
        </Headless.Menu>
      </div>
    </li>
  );
};

type UserListProps = {
  users: AgencyUserFieldsFragment[];
  onDisable?: (userId: string) => void;
  onEnable?: (userId: string) => void;
  onEdit?: (user: AgencyUserFieldsFragment) => void;
  /**
   * If true, renders a more compact version
   */
  compact?: boolean;
  /**
   * Message to display when there are no users
   */
  emptyMessage?: string;
  /**
   * The scope for permission checks (ORGANIZATION for parent agency, SUB_ORGANIZATION for child agencies)
   */
  scope: Scope;
};

/**
 * A list of users with context menus for each user
 */
export const UserList: React.FC<UserListProps> = ({
  users,
  onDisable,
  onEnable,
  onEdit,
  compact = false,
  emptyMessage = 'No users found.',
  scope
}) => {
  if (users.length === 0) {
    return (
      <p
        className={clsx(
          'text-sm text-neutral-500 dark:text-neutral-400 text-center',
          compact ? 'py-3 px-4' : 'py-4 px-4 sm:px-6'
        )}
      >
        {emptyMessage}
      </p>
    );
  }

  return (
    <ul role="list" className="divide-y divide-neutral-200 dark:divide-neutral-700">
      {[...users]
        .sort((a, b) => a.state.localeCompare(b.state))
        .map((user) => (
          <UserListItem
            key={user.id}
            user={user}
            onDisable={onDisable}
            onEnable={onEnable}
            onEdit={onEdit}
            compact={compact}
            scope={scope}
          />
        ))}
    </ul>
  );
};

const USERS_PAGE_SIZE = 25;

/** A team the list can filter to, with the ids of its members. */
export type UserListTeam = { id: string; name: string; memberIds: string[] };

type AccessFilter = 'all' | 'admin' | 'non-admin';

const matchesSearch = (user: AgencyUserFieldsFragment, search: string) => {
  const term = search.trim().toLowerCase();
  return !term || user.name.toLowerCase().includes(term) || user.email.toLowerCase().includes(term);
};

const matchesAccess = (user: AgencyUserFieldsFragment, access: AccessFilter) =>
  access === 'all' || user.roles.includes(Role.Admin) === (access === 'admin');

/**
 * A UserList with a search over name and email, filters by access and team, shown a page
 * at a time. The team filter appears only when `teams` is given.
 */
export const PagedUserList = ({
  users,
  teams,
  ...listProps
}: UserListProps & { teams?: UserListTeam[] }) => {
  const [search, setSearch] = React.useState('');
  const [access, setAccess] = React.useState<AccessFilter>('all');
  const [teamId, setTeamId] = React.useState('');
  const [page, setPage] = React.useState(0);

  const team = teams?.find((candidate) => candidate.id === teamId);
  const matching = users.filter(
    (user) =>
      matchesSearch(user, search) &&
      matchesAccess(user, access) &&
      (!team || team.memberIds.includes(user.id))
  );
  const filtered = !!search.trim() || access !== 'all' || !!team;
  const pageCount = Math.max(1, Math.ceil(matching.length / USERS_PAGE_SIZE));
  // A filter or a reload can shrink the list below the page being shown.
  const current = Math.min(page, pageCount - 1);
  const first = current * USERS_PAGE_SIZE;
  const shown = [...matching]
    .sort((a, b) => a.state.localeCompare(b.state))
    .slice(first, first + USERS_PAGE_SIZE);

  /** Sets a filter and returns to the first page, where its results start. */
  const filterBy =
    <T,>(set: (value: T) => void) =>
    (value: T) => {
      set(value);
      setPage(0);
    };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 px-4 sm:flex-row sm:px-6">
        {/* InputGroup takes no className, so the wrapper is what grows. */}
        <div className="sm:flex-1">
          <InputGroup>
            <MagnifyingGlassIcon className="h-4 w-4" />
            <Input
              type="search"
              aria-label="Search users"
              placeholder="Search by name or email..."
              value={search}
              onChange={(event) => filterBy(setSearch)(event.currentTarget.value)}
            />
          </InputGroup>
        </div>
        <div className="sm:w-40">
          <Select
            aria-label="Filter by access"
            value={access}
            onChange={(event) => filterBy(setAccess)(event.currentTarget.value as AccessFilter)}
          >
            <option value="all">All</option>
            <option value="admin">Admins</option>
            <option value="non-admin">Non-admins</option>
          </Select>
        </div>
        {teams && teams.length > 0 && (
          <div className="sm:w-44">
            <Select
              aria-label="Filter by team"
              value={teamId}
              onChange={(event) => filterBy(setTeamId)(event.currentTarget.value)}
            >
              <option value="">All teams</option>
              {teams.map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {candidate.name}
                </option>
              ))}
            </Select>
          </div>
        )}
      </div>
      <UserList
        {...listProps}
        users={shown}
        emptyMessage={filtered ? 'No users match these filters.' : listProps.emptyMessage}
      />
      {pageCount > 1 && (
        <nav aria-label="Users pages" className="flex items-center justify-center gap-1">
          {/* Fixed slots, so the count holds still when an arrow is not offered. */}
          <span className="flex w-9 justify-center">
            {current > 0 && (
              <Button plain aria-label="Previous page" onClick={() => setPage(current - 1)}>
                <ChevronLeftIcon data-slot="icon" />
              </Button>
            )}
          </span>
          <span className="min-w-24 text-center text-xs tabular-nums text-neutral-500 dark:text-neutral-400">
            {first + 1}–{first + shown.length} of {matching.length}
          </span>
          <span className="flex w-9 justify-center">
            {current < pageCount - 1 && (
              <Button plain aria-label="Next page" onClick={() => setPage(current + 1)}>
                <ChevronRightIcon data-slot="icon" />
              </Button>
            )}
          </span>
        </nav>
      )}
    </div>
  );
};
